import { useMutation } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useState, type ReactNode, type SyntheticEvent } from "react";

import { Button, buttonVariants } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader } from "../components/ui/sheet";
import { TextField } from "./billing-fields";
import {
  ROW_ACTION_LABEL,
  rowActionGuide,
  runRowAction,
  type RowAction,
  type RowActionDraft,
} from "./billing-row-actions";
import { fieldState, useFormErrors } from "./billing-validation";
import { ActionFields } from "./BillingRowActionFields";

/**
 * One invoice or subscription in a side sheet (ADR-0087): its details, with what may be done to it
 * in the footer. Choosing an action changes the sheet in place to that action's form, and Back
 * returns to the details; nothing opens on top.
 */

const DESTRUCTIVE = new Set<RowAction["kind"]>(["refund", "cancel", "void", "write_off"]);

function ActionPanel({
  customerSlug,
  action,
  kicker,
  onDone,
  onBack,
}: {
  customerSlug: string;
  action: RowAction;
  kicker: string;
  onDone: () => Promise<void>;
  onBack: () => void;
}) {
  const [draft, setDraft] = useState<RowActionDraft>({
    reason: "",
    amount: "",
    day: "",
    status: "past_due",
    // A Stripe invoice paid some other way is usually a check; a hand-recorded one may not say.
    method: action.kind === "paid_outside" ? "check" : "",
    atPeriodEnd: true,
  });
  const guide = rowActionGuide(action);
  const errors = useFormErrors(guide);
  const update = (patch: Partial<RowActionDraft>) => {
    for (const key of Object.keys(patch)) errors.clear(key);
    setDraft((current) => ({ ...current, ...patch }));
  };
  const run = useMutation({
    mutationFn: () => runRowAction(customerSlug, action, draft),
    onSuccess: onDone,
    onError: errors.fail,
  });
  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    run.mutate();
  }
  const label = ROW_ACTION_LABEL[action.kind];
  return (
    <>
      <SheetHeader title={label} kicker={kicker} />
      <SheetForm onSubmit={submit} aria-label={label} noValidate>
        <SheetBody>
          <ActionFields
            action={action}
            draft={draft}
            update={update}
            errors={errors}
            requirements={guide.requirements}
          />
          <TextField
            {...fieldState(errors, guide.requirements, "reason", "row-reason")}
            label="Reason"
            value={draft.reason}
            required
            maxLength={500}
            onChange={(reason) => {
              update({ reason });
            }}
          />
          {errors.summary ? <FieldError>{errors.summary}</FieldError> : null}
        </SheetBody>
        <SheetFooter>
          <Button type="button" variant="ghost" onClick={onBack}>
            Back
          </Button>
          <Button
            type="submit"
            variant={DESTRUCTIVE.has(action.kind) ? "destructive" : "default"}
            disabled={run.isPending}
          >
            {label}
          </Button>
        </SheetFooter>
      </SheetForm>
    </>
  );
}

export type RecordSheetContent = {
  title: string;
  kicker: string;
  details: ReactNode;
  actions: RowAction[];
  /** Stripe's own page for the record, when there is one. */
  stripeUrl: string | null;
};

function RecordPanel({
  customerSlug,
  content,
  onDone,
  onClose,
}: {
  customerSlug: string;
  content: RecordSheetContent;
  onDone: () => Promise<void>;
  onClose: () => void;
}) {
  const [active, setActive] = useState<RowAction | null>(null);
  if (active)
    return (
      <ActionPanel
        customerSlug={customerSlug}
        action={active}
        kicker={content.title}
        onBack={() => {
          setActive(null);
        }}
        onDone={async () => {
          setActive(null);
          await onDone();
        }}
      />
    );
  return (
    <>
      <SheetHeader title={content.title} kicker={content.kicker} />
      <SheetBody>{content.details}</SheetBody>
      <SheetFooter
        start={
          content.stripeUrl ? (
            <a
              className={buttonVariants({ variant: "ghost" })}
              href={content.stripeUrl}
              target="_blank"
              rel="noreferrer"
            >
              Customer&rsquo;s invoice page <ExternalLink aria-hidden />
            </a>
          ) : null
        }
      >
        {content.actions.length === 0 ? (
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        ) : (
          content.actions.map((action, index) => (
            <Button
              key={action.kind}
              type="button"
              variant={index === 0 ? "default" : "secondary"}
              onClick={() => {
                setActive(action);
              }}
            >
              {ROW_ACTION_LABEL[action.kind]}
            </Button>
          ))
        )}
      </SheetFooter>
    </>
  );
}

export function BillingRecordSheet({
  customerSlug,
  content,
  recordKey,
  onDone,
  onClose,
}: {
  customerSlug: string;
  content: RecordSheetContent | null;
  /** Remounts the sheet's state when a different record opens. */
  recordKey: string | null;
  onDone: () => Promise<void>;
  onClose: () => void;
}) {
  return (
    <Sheet open={content !== null} onClose={onClose}>
      {content ? (
        <RecordPanel
          key={recordKey}
          customerSlug={customerSlug}
          content={content}
          onDone={onDone}
          onClose={onClose}
        />
      ) : null}
    </Sheet>
  );
}

/** A label and its value, as the sheet's details list them. */
export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-4">
      <dt className="text-sm font-semibold text-muted-foreground">{label}</dt>
      <dd className="m-0">{children}</dd>
    </div>
  );
}
